import axios from "axios";
import { ensureArray } from "../helpers/typeChecking";
import { clearStoredAuthSession } from "../helpers/authStorage";
import { logApiError } from "./errorLogger";

/** Delay between retry attempts: 1s, 2s, 4s, 8s (capped). */
const backoffDelay = (attempt) =>
  new Promise((resolve) => setTimeout(resolve, Math.min(1000 * 2 ** attempt, 8000)));

// ── Global 401 Response Interceptor ──────────────────────────────────────────
// When a JWT token expires mid-session, clear auth state and redirect to login.
// Uses a module-level flag to prevent multiple simultaneous redirects.
const PUBLIC_AUTH_SUFFIXES = ["_auth_none", "_auth_onboarding", "_auth_blog"];
let isRedirecting = false;

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.response &&
      error.response.status === 401 &&
      !isRedirecting
    ) {
      const requestUrl = error.config?.url || "";
      const isPublicRoute = PUBLIC_AUTH_SUFFIXES.some((suffix) =>
        requestUrl.includes(suffix)
      );

      if (!isPublicRoute) {
        isRedirecting = true;
        clearStoredAuthSession();
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Builds the axios config object with the Authorization header from localStorage.
 * @param {number} [timeout=30000] - Request timeout in milliseconds. Defaults to 30s.
 * @returns {{ headers: { Authorization: string }, timeout: number }}
 */
const createApiConfig = (timeout = 30000, signal) => {
  const accessToken = localStorage.getItem("access_token");
  return {
    headers: { Authorization: `Bearer ${accessToken}` },
    timeout,
    ...(signal ? { signal } : {}),
  };
};

/**
 * Constructs the full API URL for a request.
 * Format: {baseURL}api/{version}/{route}/{endpoint}_auth_{authType}/{...params}?{queryString}
 * @param {string} route - API route segment (e.g. "users")
 * @param {string} endpoint - Endpoint name (e.g. "get_user")
 * @param {string} [authType='user'] - Auth type suffix appended to the endpoint
 * @param {Array} [params=[]] - Additional URL path segments appended after the endpoint
 * @param {string} [version='v1'] - API version
 * @param {Object} [queryParams={}] - Query string key/value pairs (null/undefined values are omitted)
 * @returns {string} The fully constructed URL
 */
const createFullURL = (
  route = "",
  endpoint = "",
  authType = "user",
  params = [],
  version = "v1",
  queryParams = {}
) => {
  const paramsString = ensureArray(params)
    .map((param) => `/${param}`)
    .join("");

  if (
    !["user", "advisor", "admin", "learning_lab", 'manager', "blog", "none", "onboarding", 'client', 'super_admin'].includes(
      authType
    )
  ) {
    throw new Error(
      "Auth Type must be one of: user, advisor, admin, learning_lab, manager, blog, none, onboarding, client, super_admin"
    );
  }

  if (!route || !version) {
    throw new Error(
      `Route and version are required (got route="${route}", version="${version}")`
    );
  }

  const baseURL =
    process.env.NODE_ENV === "production"
      ? "/"
      : (process.env.REACT_APP_API_URL || "http://localhost:5000/");

  // Create query string from queryParams
  const queryString = Object.keys(queryParams)
    .filter(key => queryParams[key] !== undefined && queryParams[key] !== null)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`)
    .join('&');

  const baseRoute = `${baseURL}api/${version}/${route}/${endpoint}_auth_${authType}${paramsString}`;

  return queryString ? `${baseRoute}?${queryString}` : baseRoute;
};

/** Max retry attempts on 500-level server errors before throwing. */
const maxAttempts = 5;

const isRetryableGetError = (error, { route, endpoint }) => {
  if (!axios.isAxiosError(error) || !error.response) return false;

  const status = error.response.status;
  if (status >= 500) return true;

  return (
    status === 424 &&
    route === "assemblyai" &&
    endpoint === "streaming_token"
  );
};

/**
 * Sends an authenticated GET request. Retries up to 5 times on 500-level errors.
 * @param {string} authType - Auth type suffix
 * @param {string} route - API route segment
 * @param {string} endpoint - Endpoint name
 * @param {Array} [params=[]] - URL path segments
 * @param {string} [version='v1'] - API version
 * @param {boolean} [fullRequest=false] - If true, returns the full axios response
 * @param {Object} [queryParams={}] - Query string parameters
 * @param {Object} [options={}] - Optional AbortSignal, transport timeout and retry toggle.
 * @returns {Promise<Object>} Response data (or full response if fullRequest is true)
 */
export const internalGetReq = async (
  authType,
  route,
  endpoint,
  params = [],
  version = "v1",
  fullRequest = false,
  queryParams = {},
  { signal, retry = true, timeout } = {}
) => {
  let attempts = 0;

  while (attempts < maxAttempts) {
    try {
      const fullURL = createFullURL(route, endpoint, authType, params, version, queryParams);
      const apiConfig = createApiConfig(timeout, signal);
      const response = await axios.get(fullURL, apiConfig);
      return fullRequest ? response : response.data;
    } catch (error) {
      if (signal?.aborted || error?.code === 'ERR_CANCELED') throw error;
      if (retry && isRetryableGetError(error, { route, endpoint })) {
        attempts++;
        console.error(`Attempt ${attempts} after retryable GET error:`, error.message);
        if (attempts >= maxAttempts) {
          console.error("Max retry attempts reached after retryable GET errors. Aborting...");
          // Log the error before throwing
          logApiError(error, { route, endpoint, authType, params, method: 'GET' });
          throw error;
        }
        await backoffDelay(attempts - 1);
      } else {
        // If the error is not a 500-level error, log and rethrow immediately
        logApiError(error, { route, endpoint, authType, params, method: 'GET' });
        throw error;
      }
    }
  }
};

/**
 * Sends an authenticated POST request. Retries up to 5 times on 500-level errors.
 * @param {string} authType - Auth type suffix
 * @param {string} route - API route segment
 * @param {string} endpoint - Endpoint name
 * @param {Object} [body={}] - Request body
 * @param {Array} [params=[]] - URL path segments
 * @param {Object} [queryParams={}] - Query string parameters
 * @param {string} [version='v1'] - API version
 * @param {number} [timeout] - Optional request timeout in ms; defaults to global 30s.
 * @param {boolean} [retry=true] - Whether to retry 500-level responses.
 * @param {AbortSignal} [signal] - Optional request cancellation signal.
 * @returns {Promise<Object>} Response data
 */
export const internalPostReq = async (
  authType,
  route,
  endpoint,
  body = {},
  params = [],
  queryParams = {},
  version = "v1",
  timeout,
  retry = true,
  signal
) => {
  let attempts = 0;

  while (attempts < maxAttempts) {
    try {
      const fullURL = createFullURL(route, endpoint, authType, params, version, queryParams);
      const apiConfig = createApiConfig(timeout, signal);
      const { data } = await axios.post(fullURL, body, apiConfig);
      return data;
    } catch (error) {
      if (signal?.aborted || error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        throw error;
      }
      if (
        retry &&
        axios.isAxiosError(error) &&
        error.response &&
        error.response.status >= 500
      ) {
        attempts++;
        console.error(`Attempt ${attempts} after 500-level error:`, error.message);
        if (attempts >= maxAttempts) {
          console.error("Max retry attempts reached after server errors. Aborting...");
          // Log the error before throwing
          logApiError(error, { route, endpoint, authType, params, method: 'POST' });
          throw error;
        }
        await backoffDelay(attempts - 1);
      } else {
        // If the error is not a 500-level error, log and rethrow immediately
        logApiError(error, { route, endpoint, authType, params, method: 'POST' });
        throw error;
      }
    }
  }
};

/**
 * Sends an authenticated PATCH request. Retries up to 5 times on 500-level errors.
 * @param {string} authType - Auth type suffix
 * @param {string} route - API route segment
 * @param {string} endpoint - Endpoint name
 * @param {Object} [body={}] - Fields to update
 * @param {Array} [params=[]] - URL path segments
 * @param {Object} [queryParams={}] - Query string parameters
 * @param {string} [version='v1'] - API version
 * @param {number} [timeout] - Optional request timeout in ms; defaults to global 30s.
 * @param {boolean} [retry=true] - Whether to retry 500-level responses.
 * @returns {Promise<Object>} Response data
 */
export const internalPatchReq = async (
  authType,
  route,
  endpoint,
  body = {},
  params = [],
  queryParams = {},
  version = "v1",
  timeout,
  retry = true
) => {
  let attempts = 0;
  while (attempts < maxAttempts) {
    try {
      const fullURL = createFullURL(route, endpoint, authType, params, version, queryParams);
      const apiConfig = createApiConfig(timeout);
      const { data } = await axios.patch(fullURL, body, apiConfig);
      return data;
    } catch (error) {
      if (
        retry &&
        axios.isAxiosError(error) &&
        error.response &&
        error.response.status >= 500
      ) {
        attempts++;
        console.error(`Attempt ${attempts} after 500-level error:`, error.message);
        if (attempts >= maxAttempts) {
          console.error("Max retry attempts reached after server errors. Aborting...");
          // Log the error before throwing
          logApiError(error, { route, endpoint, authType, params, method: 'PATCH' });
          throw error;
        }
        await backoffDelay(attempts - 1);
      } else {
        // If the error is not a 500-level error, log and rethrow immediately
        logApiError(error, { route, endpoint, authType, params, method: 'PATCH' });
        throw error;
      }
    }
  }
};

/**
 * Sends an authenticated PATCH request via fetch with keepalive, so the browser
 * finishes it even after the page unloads (normal XHR is aborted on tab close).
 * For last-chance saves on pagehide/visibilitychange. No retries. The body must
 * stay under the browser's keepalive budget (~64KB) or fetch rejects immediately.
 * @param {string} authType - Auth type suffix
 * @param {string} route - API route segment
 * @param {string} endpoint - Endpoint name
 * @param {Object} [body={}] - Fields to update
 * @param {Array} [params=[]] - URL path segments
 * @param {Object} [queryParams={}] - Query string parameters
 * @param {string} [version='v1'] - API version
 * @returns {Promise<Object|null>} Parsed response body, or null if not JSON
 */
export const internalKeepalivePatchReq = async (
  authType,
  route,
  endpoint,
  body = {},
  params = [],
  queryParams = {},
  version = "v1"
) => {
  const fullURL = createFullURL(route, endpoint, authType, params, version, queryParams);
  const accessToken = localStorage.getItem("access_token");
  const response = await fetch(fullURL, {
    method: "PATCH",
    keepalive: true,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error(`Keepalive PATCH ${endpoint} failed with status ${response.status}`);
  }
  return response.json().catch(() => null);
};

/**
 * Sends an authenticated DELETE request. Retries up to 5 times on 500-level errors.
 * @param {string} authType - Auth type suffix
 * @param {string} route - API route segment
 * @param {string} endpoint - Endpoint name
 * @param {Array} [params=[]] - URL path segments
 * @param {Object} [queryParams={}] - Query string parameters
 * @param {string} [version='v1'] - API version
 * @returns {Promise<Object>} Response data
 */
export const internalDeleteReq = async (
  authType,
  route,
  endpoint,
  params = [],
  queryParams = {},
  version = "v1",
  body
) => {
  let attempts = 0;

  while (attempts < maxAttempts) {
    try {
      const fullURL = createFullURL(route, endpoint, authType, params, version, queryParams);
      const apiConfig = createApiConfig();
      const { data } = await axios.delete(fullURL, {
        ...apiConfig,
        ...(body === undefined ? {} : { data: body }),
      });
      return data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response && error.response.status >= 500) {
        attempts++;
        console.error(`Attempt ${attempts} after 500-level error:`, error.message);
        if (attempts >= maxAttempts) {
          console.error("Max retry attempts reached after server errors. Aborting...");
          // Log the error before throwing
          logApiError(error, { route, endpoint, authType, params, method: 'DELETE' });
          throw error;
        }
        await backoffDelay(attempts - 1);
      } else {
        // If the error is not a 500-level error, log and rethrow immediately
        logApiError(error, { route, endpoint, authType, params, method: 'DELETE' });
        throw error;
      }
    }
  }
};
